import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Tabs,
  Tab,
} from "react-bootstrap";
import {
  FaStar,
  FaRegHeart,
  FaCheckCircle,
  FaClock,
  FaShieldAlt,
  FaHeart,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";

import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { useCheckout } from "../context/CheckoutContext";
import "./DescriptionPage.css";

const DescriptionPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { wishlist, addToWishlist, removeFromWishlist } = useWishlist();
  const { proceedToCheckout } = useCheckout();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mainImage, setMainImage] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);

  // Fetch product details from the backend
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`https://madhusewingmachines.com/api/products/getproductbyid/${productId}`);
        const productData = response.data.data;
        setProduct(productData);
        
        // Set main image - handle different path formats
        if (productData.images && productData.images.length > 0) {
          const firstImageUrl = productData.images[0].url;
          // Remove any leading path if it exists, we'll add it in the display
          const cleanUrl = firstImageUrl.replace('/uploads/images/', '').replace('/uploads/', '');
          setMainImage(cleanUrl);
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching product:", error);
        setError("Failed to load product details.");
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId]);

  // Fetch product reviews
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setReviewsLoading(true);
        console.log('Fetching product reviews for:', productId);
        const response = await axios.get(`https://madhusewingmachines.com/api/product-reviews/product/${productId}`);
        console.log('Product reviews response:', response.data);
        
        if (response.data.success) {
          const reviewsData = response.data.data;
          setReviews(reviewsData);
          setTotalReviews(reviewsData.length);
          
          // Calculate average rating
          if (reviewsData.length > 0) {
            const sum = reviewsData.reduce((acc, review) => acc + review.rating, 0);
            const avg = sum / reviewsData.length;
            setAverageRating(avg);
          } else {
            setAverageRating(0);
          }
          
          console.log('Product reviews set:', reviewsData);
        }
      } catch (error) {
        console.error("Error fetching product reviews:", error);
        console.error("Error details:", error.response?.data);
      } finally {
        setReviewsLoading(false);
      }
    };

    if (productId) {
      fetchReviews();
    }
  }, [productId]);

  // Check if the product is in the wishlist
  const isInWishlist = wishlist.some((item) => item._id === product?._id);

  // Render error message if product is not found
  if (loading) {
    return (
      <Container className="py-5">
        <p>Loading product details...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <h2>{error}</h2>
        <Button variant="primary" onClick={() => navigate("/")}>
          Back to Products
        </Button>
      </Container>
    );
  }

  if (!product) {
    return (
      <Container className="py-5">
        <h2>Product Not Found</h2>
        <Button variant="primary" onClick={() => navigate("/")}>
          Back to Products
        </Button>
      </Container>
    );
  }

  // Handlers
  const handleThumbnailClick = (thumb) => {
    // When clicking a thumbnail, swap it with the current main image
    const currentMainImage = mainImage;
    const clickedImageUrl = thumb.url.replace('/uploads/images/', '').replace('/uploads/', '');
    
    // Set the clicked image as the new main image
    setMainImage(clickedImageUrl);
    
    // Update the product images array to reflect the swap
    if (product && product.images) {
      const updatedImages = [...product.images];
      const clickedIndex = updatedImages.findIndex(img => {
        const cleanUrl = img.url.replace('/uploads/images/', '').replace('/uploads/', '');
        return cleanUrl === clickedImageUrl;
      });
      const mainIndex = updatedImages.findIndex(img => {
        const cleanUrl = img.url.replace('/uploads/images/', '').replace('/uploads/', '');
        return cleanUrl === currentMainImage;
      });
      
      // Swap the images in the array
      if (clickedIndex !== -1 && mainIndex !== -1) {
        [updatedImages[clickedIndex], updatedImages[mainIndex]] = [updatedImages[mainIndex], updatedImages[clickedIndex]];
        setProduct({ ...product, images: updatedImages });
      }
    }
  };


  const handleAddToCart = () => {
    addToCart({ 
      ...product, 
      quantity,
      type: 'product' // Explicitly set type to 'product'
    });
    toast.success("Product added to Cart Successfully!", {
      position: "top-center",
      autoClose: 2000,
    });
  };

  const handleWishlistToggle = () => {
    if (isInWishlist) {
      removeFromWishlist(product._id);
      toast.info("💔 Removed from Wishlist!", {
        position: "top-center",
        autoClose: 2000,
      });
    } else {
      addToWishlist(product);
      toast.success("❤️ Added to Wishlist!", {
        position: "top-center",
        autoClose: 2000,
      });
    }
  };

  const handleBuyNow = () => {
    const cartItem = { 
      ...product, 
      quantity,
      type: 'product' // Explicitly set type to 'product' (overrides product.type which is category)
    };
    proceedToCheckout([cartItem]);
    navigate("/checkout");
  };

  const pageStyle = {
    background: "linear-gradient(135deg, #e6f7ff, #f0f0f0)",
    minHeight: "100vh",
    padding: "20px",
  };

  return (
    <div style={pageStyle}>
      <Container
        className="py-4 ultimate-description"
        style={{
          background: "linear-gradient(135deg, #e6f7ff, #f0f0f0)",
          minHeight: "100vh",
          padding: "20px",
          marginTop: "80px",
        }}
      >
        <Row>
          {/* Left Section - Product Images */}
          <Col xs={12} md={6} className="mb-4">
            <div className="position-relative image-container shadow-sm rounded overflow-hidden">
              <span className="badge bg-danger position-absolute top-0 start-0 m-2 p-2 fs-6">
                NEW
              </span>
              <Card className="border-0">
                <Card.Img
                  src={`https://madhusewingmachines.com/uploads/${mainImage}` }
                  alt={product.name}
                  className="img-fluid rounded"
                />
              </Card>
            </div>
            <Row className="mt-3 g-2">
              {product.images.slice(1, 5).map((thumb, index) => (
                <Col key={index} xs={3}>
                  <img
                    src={`https://madhusewingmachines.com/uploads/${thumb.url}`}
                    alt={`${product.name} view ${index + 1}`}
                    className={`img-thumbnail cursor-pointer ${mainImage === thumb ? "border-primary" : ""
                      }`}
                    onClick={() => handleThumbnailClick(thumb)}
                    style={{
                      transition: "transform 0.3s",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.transform = "scale(1.05)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.transform = "scale(1)")
                    }
                  />
                </Col>
              ))}
            </Row>
          </Col>

          {/* Right Section - Product Details */}
          <Col xs={12} md={6}>
            <h2 className="mb-3 fw-bold" style={{ fontSize: "1.5rem" }}>
              {product.name}
            </h2>
            <p className="lead text-muted" style={{ fontSize: "1rem" }}>
              {product.description}
            </p>

            {/* Rating Section */}
            <div className="d-flex align-items-center mb-3">
              <div className="text-warning me-2">
                {[...Array(5)].map((_, i) => (
                  <FaStar
                    key={i}
                    className={
                      i < Math.floor(averageRating) ? "text-warning" : "text-muted"
                    }
                  />
                ))}
              </div>
              <span className="fw-bold me-2 h5" style={{ fontSize: "1.2rem" }}>
                {totalReviews > 0 ? averageRating.toFixed(1) : "No ratings yet"}
              </span>
              {totalReviews > 0 && (
                <span className="text-muted" style={{ fontSize: "0.9rem" }}>
                  ({totalReviews} {totalReviews === 1 ? 'review' : 'reviews'})
                </span>
              )}
            </div>

            {/* Price Section */}
            <div className="mb-3">
              <span
                className="h3 fw-bold text-success me-2"
                style={{ fontSize: "1.5rem" }}
              >
                ₹{product.price}
              </span>
              <span
                className="text-muted text-decoration-line-through h5 me-2"
                style={{ fontSize: "1rem" }}
              >
                ₹{product.originalPrice}
              </span>
              <span
                className="badge bg-success fs-6"
                style={{ fontSize: "0.9rem" }}
              >
                {product.discount}
              </span>
              <div
                className="text-muted small mt-1"
                style={{ fontSize: "0.8rem" }}
              >
                (Net Quantity: 1/1) (Incl. all taxes)
              </div>
            </div>

            {/* Delivery Info */}
            <div className="mb-4">
              <Row className="g-2">
                <Col xs={12} md={4} className="d-flex align-items-center">
                  <FaCheckCircle className="text-success me-2" size={20} />
                  <span style={{ fontSize: "0.9rem" }}>Free Delivery</span>
                </Col>
                <Col xs={12} md={4} className="d-flex align-items-center">
                  <FaClock className="text-secondary me-2" size={20} />
                  <span style={{ fontSize: "0.9rem" }}>3-5 Days</span>
                </Col>
                <Col xs={12} md={4} className="d-flex align-items-center">
                  <FaShieldAlt className="text-secondary me-2" size={20} />
                  <span style={{ fontSize: "0.9rem" }}>2 Year Warranty</span>
                </Col>
              </Row>
            </div>

            {/* Quantity Selector */}
            <div className="d-flex align-items-center mb-4">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}
              >
                &minus;
              </Button>
              <span className="mx-3 h5" style={{ fontSize: "1.2rem" }}>
                {quantity}
              </span>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setQuantity((prev) => prev + 1)}
              >
                +
              </Button>
            </div>

            {/* Action Buttons */}
            <div className="d-flex gap-2 mb-4">
              <Button
                variant="primary"
                className="flex-grow-1 shadow"
                onClick={handleAddToCart}
              >
                Add to Cart
              </Button>
              <Button
                variant="info"
                className="flex-grow-1 shadow text-white"
                onClick={handleBuyNow}
              >
                Buy Now
              </Button>
              <Button
                variant="outline-secondary"
                className="shadow"
                onClick={handleWishlistToggle}
              >
                {isInWishlist ? (
                  <FaHeart size={20} color="red" /> // Filled heart if in wishlist
                ) : (
                  <FaRegHeart size={20} /> // Outlined heart if not in wishlist
                )}
              </Button>
            </div>

            {/* Tabs Section with Custom Styling */}
            <Tabs defaultActiveKey="description" className="mb-4 custom-tabs" fill>
              <Tab eventKey="description" title="Description">
                <div className="p-3">
                  <h4 className="fw-bold mb-3" style={{ fontSize: "1.2rem" }}>
                    Product Description
                  </h4>
                  <p className="text-muted" style={{ fontSize: "0.9rem" }}>
                    {product.description}
                  </p>
                  <h5 className="fw-bold mt-4" style={{ fontSize: "1.1rem" }}>
                    Key Features
                  </h5>
                  <Row>
                    {product.features.map((feature, index) => (
                      <Col md={6} key={index} className="mb-3">
                        <div className="d-flex align-items-start">
                          <FaCheckCircle className="text-success me-2" size={20} />
                          <div>
                            <strong style={{ fontSize: "1rem" }}>
                              {feature}
                            </strong>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              </Tab>
              
              {product.demoVideo && (
                <Tab eventKey="video" title="Demo Video">
                  <div className="p-3">
                    <h4 className="fw-bold mb-3" style={{ fontSize: "1.2rem" }}>
                      Product Demo Video
                    </h4>
                    <div className="alert alert-info">
                      <p className="mb-2">Watch the product demonstration video:</p>
                      <a 
                        href={product.demoVideo} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="btn btn-primary"
                      >
                        <i className="fas fa-play-circle me-2"></i>
                        Watch Demo Video
                      </a>
                    </div>
                    <small className="text-muted">
                      Click the button above to watch the video in a new tab
                    </small>
                  </div>
                </Tab>
              )}
              
              <Tab eventKey="specifications" title="Specifications">
                <div className="p-3">
                  <h4 className="fw-bold mb-3" style={{ fontSize: "1.2rem" }}>
                    Technical Specifications
                  </h4>
                  <Row className="gy-3">
                    <Col md={6}>
                      <strong style={{ fontSize: "1rem" }}>Machine Type</strong>
                      <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                        {product.specification.machineType}
                      </div>
                    </Col>
                    <Col md={6}>
                      <strong style={{ fontSize: "1rem" }}>
                        Number of Stitches
                      </strong>
                      <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                        {product.specification.numberOfStitches}
                      </div>
                    </Col>
                  </Row>
                  <Row className="gy-3 mt-2">
                    <Col md={6}>
                      <strong style={{ fontSize: "1rem" }}>
                        Maximum Stitch Width
                      </strong>
                      <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                        {product.specification.maxStitchWidth}
                      </div>
                    </Col>
                    <Col md={6}>
                      <strong style={{ fontSize: "1rem" }}>
                        Maximum Stitch Length
                      </strong>
                      <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                        {product.specification.maxStitchLength}
                      </div>
                    </Col>
                  </Row>
                  <Row className="gy-3 mt-2">
                    <Col md={6}>
                      <strong style={{ fontSize: "1rem" }}>
                        Needle Threading System
                      </strong>
                      <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                        {product.specification.needleThreadingSystem}
                      </div>
                    </Col>
                    <Col md={6}>
                      <strong style={{ fontSize: "1rem" }}>Bobbin System</strong>
                      <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                        {product.specification.bobbinSystem}
                      </div>
                    </Col>
                  </Row>
                  <Row className="gy-3 mt-2">
                    <Col md={6}>
                      <strong style={{ fontSize: "1rem" }}>Motor Speed</strong>
                      <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                        {product.specification.motorSpeed}
                      </div>
                    </Col>
                    <Col md={6}>
                      <strong style={{ fontSize: "1rem" }}>Presser Foot</strong>
                      <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                        {product.specification.presserFoot}
                      </div>
                    </Col>
                  </Row>
                  <Row className="gy-3 mt-2">
                    <Col md={6}>
                      <strong style={{ fontSize: "1rem" }}>Free Arm</strong>
                      <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                        {product.specification.freeArm}
                      </div>
                    </Col>
                    <Col md={6}>
                      <strong style={{ fontSize: "1rem" }}>Light</strong>
                      <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                        {product.specification.light}
                      </div>
                    </Col>
                  </Row>
                  <Row className="gy-3 mt-2">
                    <Col md={6}>
                      <strong>Weight</strong>
                      <div className="text-muted">
                        {product.specification.weight}
                      </div>
                    </Col>
                    <Col md={6}>
                      <strong>Warranty</strong>
                      <div className="text-muted">
                        {product.specification.warranty}
                      </div>
                    </Col>
                  </Row>
                </div>
              </Tab>
              <Tab eventKey="reviews" title={`Reviews (${reviews.length})`}>
                <div className="p-3">
                  <h4 className="fw-bold mb-3" style={{ fontSize: "1.2rem" }}>
                    Customer Reviews
                  </h4>
                  
                  {reviewsLoading ? (
                    <div className="text-center py-4">
                      <p>Loading reviews...</p>
                    </div>
                  ) : reviews.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-muted">No reviews yet. Be the first to review this product!</p>
                    </div>
                  ) : (
                    <div>
                      {reviews.map((review, index) => (
                        <Card key={index} className="mb-3 shadow-sm">
                          <Card.Body>
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div>
                                <h6 className="mb-1 fw-bold">{review.name}</h6>
                                <small className="text-muted">{review.location}</small>
                                {review.verified && (
                                  <span className="badge bg-success ms-2" style={{ fontSize: "0.7rem" }}>
                                    ✓ Verified Purchase
                                  </span>
                                )}
                              </div>
                              <div className="text-warning">
                                {[...Array(5)].map((_, i) => (
                                  <FaStar
                                    key={i}
                                    className={i < review.rating ? "text-warning" : "text-muted"}
                                    size={16}
                                  />
                                ))}
                              </div>
                            </div>
                            <p className="mb-2" style={{ fontSize: "0.95rem" }}>
                              {review.review}
                            </p>
                            <small className="text-muted">
                              {new Date(review.createdAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </small>
                          </Card.Body>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              </Tab>
            </Tabs>
          </Col>
        </Row>

        {/* Toast Container */}
        <ToastContainer position="top-center" autoClose={2000} />
      </Container>
    </div>
  );
};

export default DescriptionPage;